import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import Select from 'react-select';
import type { StylesConfig, MultiValue } from 'react-select';
import SummernoteCorgee, { type SummernoteCorgeeRef } from '../Globals/SummernoteCorgee';
import CorgeeModal, { type CorgeeModalRef } from '../Globals/CorgeeModal';
import CorgeeWarning from '../Globals/CorgeeWarning';
import FileUpload, { type FileUploadRef } from '../Globals/FileUpload';
import CustomFields from '../CustomFields/CustomFields';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

import { editCardService } from '../../services/editCardService';
import ConversationApi from '../ConversationItem/api/ConversationApi';
import { conversationBus } from '../../lib/conversationBus';
import { MAX_BODY_BYTES, BODY_TOO_LARGE_MESSAGE } from '../../lib/constants';
import { byteLength } from '../../lib/utils';
import CardConversationLayers from '../Globals/CardConversationLayers';
import './EditCard.css';
import type { SideMenuItem } from '@/services/menuService';
import type { Conversation, ConversationDetails } from '@/types/conversation';
import { getPersonId } from '../utils/CorgeeHelper';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import { dashboardGroupsService } from '../DashboardGroups';

import { dashboardService } from '@/services/dashboardService';
import { apiService } from '@/services/api';
import { socketService } from '@/services/socketService';

export interface EditCardConfig {
  apiUrl: string;
  authToken: string;
  SocketConnectionId?: string;
  attachReactFunctions?: (funcs: any) => void;
  functions: {
    loadingBarStart?: () => void;
    loadingBarComplete?: () => void;
    conversationResponse?: (requestConfig: any, response: any) => void;
    onUpdateConversation?: (id: number, attrs: any, b: boolean, scope: string) => void;
    addLayerToCard?: (e: any, conversation: any, layer: any, cb: (layerId: number) => void) => void;
  };
  params: {
    TitleCard?: string;
    SupportLayer?: string;
    SupportConfigValue?: string;
    SupportProjectId?: number;
    Conversation: any;
  };
}

export interface EditCardProps {
  config: EditCardConfig;
  userData?: any;
  UpdateCustomFields?: any;
}

export interface EditCardRef {
  openModal: (conv?: any) => void;
  closeModal: () => void;
}

const SAVE_FAILED_MESSAGE =
  'The card could not be saved. If it contains pasted images, try removing them or attaching the file instead.';

export interface DuplicateCardRef {
  openModal: (conv?: any) => void;
  closeModal: () => void;
}

// Returns only the default security group (C2_defaultGroupId, else IsDefault).
const getDefaultGroups = (fetchedGroups: any[]): any[] =>
{
  const defaultGroupId = Number(CorgeeStorageHelper.defaultGroupId());
  let defaultGroups = fetchedGroups.filter(
    (g: any) => (g.GroupId ?? g.Id) === defaultGroupId,
  );
  if (defaultGroups.length === 0)
  {
    const fallback = fetchedGroups.find((g: any) => g.IsDefault === true);
    defaultGroups = fallback ? [fallback] : [];
  }
  return defaultGroups;
};

const EditCard = forwardRef<EditCardRef, EditCardProps>(({ config, UpdateCustomFields }, ref) =>
{
  const [api] = useState({ url: config.apiUrl, token: config.authToken });
  const [conversation, setConversation] = useState<any>(config.params.Conversation || {});
  const [emptyConversation] = useState<any>(config.params.Conversation || {});
  const [groups, setGroups] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const [layersSelected, setLayersSelected] = useState<any[]>([]);
  const [groupIdsSelected, setGroupIdsSelected] = useState<number[]>([]);
  const [groupsSelected, setGroupsSelected] = useState<any[]>([]);
  // Ensures the default layer is auto-picked only once per new card / project.
  const hasAutoPickedLayerRef = useRef(false);

  const [showTitleWarning, setShowTitleWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Save failures that aren't title/layer related
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  const [showGroups, setShowGroups] = useState(false);
  const [showCustomFields, setShowCustomFields] = useState(true);
  const [showSummernote, setShowSummernote] = useState(true);
  const [showLayerWarning, setShowLayerWarning] = useState(false);
  // Computed: show warning when a selected group has NONE of the selected layers
  const showGroupWarning = useMemo(() =>
  {
    if ((conversation.ConfigValueId ?? conversation.ConfigValueID) !== 9) return false;
    if (groupsSelected.length === 0 || layersSelected.length === 0) return false;
    const selectedLayerIds = new Set(layersSelected.map((l: any) => l.LayerId));
    // Cross-reference with full groups catalog to get GroupLayers
    const selectedGroupIds = new Set(groupsSelected.map((g: any) => g.GroupId ?? g.Id));
    const fullSelectedGroups = groups.filter((g: any) => selectedGroupIds.has(g.GroupId ?? g.Id));
    return fullSelectedGroups.some((group: any) =>
    {
      const groupLayerIds: number[] = (group.GroupLayers || []).map((gl: any) => gl.LayerId);
      return !groupLayerIds.some((id: number) => selectedLayerIds.has(id));
    });
  }, [
    conversation.ConfigValueId,
    conversation.ConfigValueID,
    groups,
    groupsSelected,
    layersSelected,
  ]);

  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isImportant, setIsImportant] = useState<boolean>(
    config.params.Conversation?.IsImportant || false,
  );

  const [layersCustomFieldsOriginal, setLayersCustomFieldsOriginal] = useState<any[]>([]);
  const [layersCustomFieldsChanges, setLayersCustomFieldsChanges] = useState<any[]>([]);
  const [isEnabledSaveAndNew, setIsEnabledSaveAndNew] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null);
  const [editingPhaseTitle, setEditingPhaseTitle] = useState('');
  const [creatingCard, setCreatingCard] = useState<boolean>(false);
  const [currentItemType, setCurrentItemType] = useState<SideMenuItem | null>(null);
  const [disableEditProject, setDisableEditProject] = useState(true);
  const [duplicateProjectCards, setDuplicateProjectCards] = useState(false);

  const activePhase = useMemo(() =>
  {
    if (tabIndex > 0 && conversation?.Phases && conversation.Phases.length >= tabIndex)
    {
      return conversation.Phases[tabIndex - 1];
    }
    return {
      ConversationPhaseId: 0,
      CoreTaskId: conversation?.CoreTaskId ?? conversation?.CoreTaskID,
    };
  }, [tabIndex, conversation?.Phases, conversation?.CoreTaskId, conversation?.CoreTaskID]);

  const acceptPhaseRename = useCallback(
    (phase: any) =>
    {
      if (!editingPhaseTitle.trim())
      {
        setEditingPhaseId(null);
        return;
      }

      const newPhases = [...(conversation.Phases || [])];
      const found = newPhases.find((p: any) => p.ConversationPhaseId === phase.ConversationPhaseId);
      if (found)
      {
        found.Title = editingPhaseTitle;
      }
      setConversation((prev: any) => ({ ...prev, Phases: newPhases }));
      setEditingPhaseId(null);

      const item = {
        ConversationId: phase.ConversationId || phase.ConversationID || conversation.ConversationId,
        ConversationPhaseId: phase.ConversationPhaseId,
        Title: editingPhaseTitle,
      };
      ConversationApi.changeNameCard(api, item).catch((e: any) => console.error(e));
    },
    [api, conversation, editingPhaseTitle],
  );

  const modalRef = useRef<CorgeeModalRef>(null);
  const addLayerModalRef = useRef<CorgeeModalRef>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const summernoteRef = useRef<SummernoteCorgeeRef>(null);
  const bodyRef = useRef<string>('');
  const fileUploadRef = useRef<FileUploadRef>(null);

  const onDropFiles = useCallback((files: File[]) =>
  {
    fileUploadRef.current?.onDrop(files);
  }, []);

  const [pendingAddLayer, setPendingAddLayer] = useState<any>(null);
  const [pendingAddLayerCallback, setPendingAddLayerCallback] = useState<any>(null);

  const handleConfirmAddLayer = useCallback(() =>
  {
    if (!pendingAddLayer) return;

    const conversationCopy = { ...conversation };
    conversationCopy.ConversationLayers = [...(conversationCopy.ConversationLayers || [])];

    if (
      !conversationCopy.ConversationLayers.some((l: any) => l.LayerId === pendingAddLayer.LayerId)
    )
    {
      conversationCopy.ConversationLayers.push(pendingAddLayer);
    }

    if (conversationCopy.ConversationId)
    {
      editCardService
        .UpdateProject(conversationCopy)
        .then(() =>
        {
          if (pendingAddLayerCallback && pendingAddLayerCallback.onUpdateLayerToCard)
          {
            pendingAddLayerCallback.onUpdateLayerToCard(pendingAddLayer.LayerId);
          }
          setConversation(conversationCopy);
          const selected = conversationCopy.ConversationLayers || [];
          setLayersSelected(selected);
          setShowLayerWarning(selected.length === 0);

          if (config.functions?.onUpdateConversation)
          {
            config.functions.onUpdateConversation(
              conversation.ConversationId,
              { ConversationLayers: conversationCopy.ConversationLayers },
              true,
              '',
            );
          }

          addLayerModalRef.current?.closeModal();
        })
        .catch(err =>
        {
          console.error('Failed to add layer to card:', err);
          addLayerModalRef.current?.closeModal();
        });
    }
    else
    {
      // New card not yet saved, apply locally
      setConversation(conversationCopy);
      const selected = conversationCopy.ConversationLayers || [];
      setLayersSelected(selected);
      setShowLayerWarning(selected.length === 0);
      if (pendingAddLayerCallback && pendingAddLayerCallback.onUpdateLayerToCard)
      {
        pendingAddLayerCallback.onUpdateLayerToCard(pendingAddLayer.LayerId);
      }
      addLayerModalRef.current?.closeModal();
    }
  }, [pendingAddLayer, pendingAddLayerCallback, conversation, config]);

  const InitConfig = useCallback(
    (conv: any, itemType?: SideMenuItem) =>
    {
      // Deep-clone to isolate EditCard state from the parent (InfoWindow/ConversationDetailsView)
      // so that edits here don't leak until "Update" is clicked
      const clonedConv = JSON.parse(JSON.stringify(conv));
      bodyRef.current = clonedConv.Body ?? '';

      // Reset so the default layer is re-picked for this freshly opened card.
      hasAutoPickedLayerRef.current = false;

      // A new project (ConfigValueId 9) is top-level and must not inherit a parent ProjectId.
      const newCardConfigValueId = itemType
        ? (itemType.ConfigValueID || conv.ConfigValueID)
        : (conv.ConfigValueId ?? conv.ConfigValueID);
      const isNewProjectCard = !!itemType && newCardConfigValueId === 9;
      const effectiveProjectId = isNewProjectCard ? 0 : conv.ProjectId;

      if (!itemType)
      {
        // Edit existing conversation
        setConversation(clonedConv);
        setDisableEditProject(false);
        setShowSummernote(!clonedConv.duplicateProject);
        if (clonedConv.duplicateProject) setDuplicateProjectCards(true);
        setCurrentItemType(null);
      }
      else if (!conv.ConversationId)
      {
        // Create Conversation from blue button
        conv.IsImportant = false;
        setCreatingCard(true);
        setCurrentItemType(itemType);
        setDisableEditProject(false);
        setConversation({
          ...emptyConversation,
          ConfigValueId: itemType.ConfigValueID || conv.ConfigValueID,
          Icon: itemType.Icon,
          PersonId: getPersonId(),
          ProjectId: effectiveProjectId || 0,
          // Start empty — CardConversationLayers picks the correct layer after
          // GetLayersByConfigValueId + GetLayersForCardBy resolve.
          ConversationLayers: [],
        });
      }
      else
      {
        // Create Conversation from other conversation
        setCreatingCard(true);
        setCurrentItemType(itemType);
        setDisableEditProject(false);
        setConversation({
          ...emptyConversation,
          RelatedConversationId: conv.ConversationId,
          ConfigValueId: itemType.ConfigValueID || conv.ConfigValueID,
          Icon: itemType.Icon,
          PersonId: getPersonId(),
          ProjectId: effectiveProjectId,
          // Start empty — CardConversationLayers picks the correct layer after
          // GetLayersByConfigValueId + GetLayersForCardBy resolve.
          ConversationLayers: [],
        });
      }
      setGroups([]);
      setProjects([]);
      setSelectedProject(null);
      setLayersCustomFieldsOriginal([]);
      setLayersCustomFieldsChanges([]);
      !itemType && setIsImportant(conv.IsImportant);
      setShowGroups(itemType ? true : false);

      // Use the phaseSelected prop to set the initial tabIndex for EditCard
      let initialTabIndex = 0;
      if (
        !itemType &&
        conv.phaseSelected &&
        conv.phaseSelected.ConversationPhaseId > 0 &&
        Array.isArray(conv.Phases)
      )
      {
        const foundIndex = conv.Phases.findIndex(
          (p: any) => p.ConversationPhaseId === conv.phaseSelected.ConversationPhaseId,
        );
        if (foundIndex >= 0)
        {
          initialTabIndex = foundIndex + 1;
        }
      }
      setTabIndex(initialTabIndex);
      setEditingPhaseId(null);

      // Fetch projects for this card type
      const cvId =
        itemType && !conv.ConversationId
          ? itemType.ConfigValueID
          : (conv.ConfigValueId ?? conv.ConfigValueID);
      if (cvId)
      {
        editCardService
          .GetProjectsByConfigValueId(cvId)
          .then(projectList =>
          {
            const list = projectList || [];
            setProjects(list);
            const current = list.find((p: any) => (p.ProjectId ?? p.Id) === effectiveProjectId);
            setSelectedProject(current || null);
          })
          .catch(() =>
          {
            /* silently ignore */
          });
      }

      // Pre-populate groups from existing conversation
      const existingGroups: any[] = conv.ConversationGroups || [];
      setGroupsSelected(existingGroups);
      setGroupIdsSelected(existingGroups.map((g: any) => g.GroupId ?? g.Id));

      // Pre-populate layers from existing conversation (exclude virtual layer 0).
      // For new cards (itemType), start empty: CardConversationLayers will call
      // onChangedLayers with the correct layer once GetLayersByConfigValueId resolves.
      const existingLayers: any[] = itemType
        ? []
        : (conv.ConversationLayers || []).filter((l: any) => l.LayerId !== 0);
      setLayersSelected(existingLayers);

      const isProject = (conv.ConfigValueId ?? conv.ConfigValueID) === 9;

      // itemType is only present when creating a new card.
      const isNewCard = !!itemType;

      if (isProject)
      {
        // For projects: fetch ALL groups as options, project groups as selected
        Promise.all([
          editCardService.GetGroups(), // All groups (catalog)
          editCardService.GetGroups(effectiveProjectId), // Project-specific groups
        ]).then(([allGroups, projectGroups]) =>
        {
          setGroups(allGroups || []);
          const selected = projectGroups || [];
          if (existingGroups.length === 0 && selected.length > 0)
          {
            // New card: only the default group; editing: keep all project groups.
            const toSelect = isNewCard ? getDefaultGroups(selected) : selected;
            setGroupsSelected(toSelect);
            setGroupIdsSelected(toSelect.map((g: any) => g.GroupId ?? g.Id));
          }
          setShowGroups(true);
        });
      }
      else
      {
        // For non-projects: fetch project groups as both options and selected
        editCardService.GetGroups(effectiveProjectId).then(data =>
        {
          const fetchedGroups = data || [];
          setGroups(fetchedGroups);
          if (existingGroups.length === 0 && fetchedGroups.length > 0)
          {
            // New card: only the default group; editing: keep all fetched groups.
            const toSelect = isNewCard ? getDefaultGroups(fetchedGroups) : fetchedGroups;
            setGroupsSelected(toSelect);
            setGroupIdsSelected(toSelect.map((g: any) => g.GroupId ?? g.Id));
          }
          setShowGroups(true);
        });
      }

      if (conv.initialFiles && conv.initialFiles.length > 0)
      {
        setConversation((prev: any) => ({
          ...prev,
          Attachments: [...conv.initialFiles],
        }));
      }
    },
    [emptyConversation],
  );

  useImperativeHandle(ref, () => ({
    openModal: (conv?: Conversation, itemType?: SideMenuItem) =>
    {
      if (conv && Object.keys(conv).length > 0)
      {
        InitConfig(conv, itemType);
      }

      setShowTitleWarning(false);
      setSaveWarning(null);
      setIsSaving(false);
      setIsUploadingFile(false);
      setIsEnabledSaveAndNew(false);

      if (modalRef.current)
      {
        modalRef.current.openModal(() =>
        {
          setTimeout(() =>
          {
            inputRef.current?.focus();
          }, 0);
        });
      }
    },
    closeModal: () =>
    {
      if (modalRef.current)
      {
        modalRef.current.closeModal();
      }
      setConversation({ ...emptyConversation });
    },
  }));

  const ReloadConfig = useCallback(() =>
  {
    setConversation({ ...emptyConversation });
    setGroups([]);
    setLayersSelected([]);
    setShowCustomFields(false);
    setGroupIdsSelected([]);
    setGroupsSelected([]);
    setShowGroups(false);

    const isProject = (emptyConversation.ConfigValueId ?? emptyConversation.ConfigValueID) === 9;

    if (isProject)
    {
      Promise.all([
        editCardService.GetGroups(),
        editCardService.GetGroups(emptyConversation.ProjectId),
      ]).then(([allGroups, projectGroups]) =>
      {
        setGroups(allGroups || []);
        const selected = projectGroups || [];
        setGroupsSelected(selected);
        setGroupIdsSelected(selected.map((g: any) => g.GroupId ?? g.Id));
        setShowGroups(true);
        setShowCustomFields(true);
      });
    }
    else
    {
      editCardService.GetGroups(emptyConversation.ProjectId).then(data =>
      {
        const fetchedGroups = data || [];
        setGroups(fetchedGroups);
        setGroupsSelected(fetchedGroups);
        setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
        setShowGroups(true);
        setShowCustomFields(true);
      });
    }
  }, [emptyConversation]);

  useEffect(() =>
  {
    if (config.attachReactFunctions)
    {
      config.attachReactFunctions({
        initConfig: InitConfig,
        reloadConfig: ReloadConfig,
      });
    }

    // Initial fetch if this is active
    if (conversation.ProjectId)
    {
      const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;

      if (isProject)
      {
        Promise.all([
          editCardService.GetGroups(),
          editCardService.GetGroups(conversation.ProjectId),
        ]).then(([allGroups, projectGroups]) =>
        {
          setGroups(allGroups || []);
          const selected = projectGroups || [];
          if (selected.length > 0)
          {
            setGroupsSelected(selected);
            setGroupIdsSelected(selected.map((g: any) => g.GroupId ?? g.Id));
          }
          setShowGroups(true);
        });
      }
      else
      {
        editCardService.GetGroups(conversation.ProjectId).then(data =>
        {
          const fetchedGroups = data || [];
          setGroups(fetchedGroups);
          if (fetchedGroups.length > 0)
          {
            setGroupsSelected(fetchedGroups);
            setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
          }
          setShowGroups(true);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Reset = () =>
  {
    setConversation((prev: any) => ({
      ...prev,
      Title: '',
      Body: '',
      Attachments: [],
      IsRedFlag: false,
    }));
    bodyRef.current = '';
    summernoteRef.current?.clearContent();
    fileUploadRef.current?.reset();
  };

  // Stable identities: internalConfig must not change on every render.
  const onLoadDataCallback = useCallback((
    _elementRef: any,
    reactParamsObjects: any,
    _reactParamsFunctions: any,
  ) =>
  {
    setLayersCustomFieldsOriginal(JSON.parse(JSON.stringify(reactParamsObjects.data)));
    setConversation((prev: ConversationDetails) => ({
      ...prev,
      CustomFields: JSON.parse(JSON.stringify(reactParamsObjects.data)),
    }));
  }, []);

  const UpdateDetailsConversationReact = useCallback((selectedProject: any) =>
  {
    setLayersCustomFieldsChanges(selectedProject);
  }, []);

  const uptateTitle = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const val = e.target.value;
    setConversation((prev: any) => ({ ...prev, Title: val }));
    setShowTitleWarning(val.trim() === '');
  };

  const onMarkAsRedFlag = (e: React.MouseEvent) =>
  {
    e.preventDefault();
    setConversation((prev: any) => ({ ...prev, IsRedFlag: !prev.IsRedFlag }));
  };

  const onChangeFinished = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    setConversation((prev: any) => ({ ...prev, Finished: e.target.checked }));
  };

  // Held in a ref, not state: typing must not re-render the modal (custom
  // fields, layers, tabs). The body is read back from the editor on save.
  const onChangeSummernote = (content: string) =>
  {
    bodyRef.current = content.replace('&amp;', '&');
  };

  const onChangeFileUpload = (filesUploaded: boolean | any[]) =>
  {
    if (filesUploaded === true)
    {
      setIsUploadingFile(true);
    }
    else
    {
      setConversation((prev: any) => ({ ...prev, Attachments: filesUploaded as any[] }));
      setIsUploadingFile(false);
    }
  };

  const onRemoveAttachment = (index: number) =>
  {
    setConversation((prev: any) =>
    {
      const newAtt = [...(prev.Attachments || [])];
      newAtt.splice(index, 1);
      return { ...prev, Attachments: newAtt };
    });
  };

  const onUpdateTemporalImportant = (imp: boolean) =>
  {
    setIsImportant(imp);
  };

  const getTagsInBody = (bodyTags: string) =>
  {
    // simplified version. Original relied on jQuery parsing `$('<div>' + bodyTags + '</div>')`
    const listTags: any[] = [];
    try
    {
      const matchTags = bodyTags.match(/person-id="(\d+)"/g);
      if (matchTags)
      {
        matchTags.forEach(m =>
        {
          const id = m.replace(/[^0-9]/g, '');
          listTags.push({
            PersonId: parseInt(id, 10),
            ConversationId: 0,
            ReplyId: 0,
            ReplyFollowing: 0,
          });
        });
      }
    }
    catch (e)
    {
      console.warn(e);
    }
    return listTags;
  };

  const createRequest = (conv: ConversationDetails) =>
  {
    const payload = {
      ConversationId: 0,
      Attachments: conv.Attachments || [],
      IsFromProject: false,
      ConfigValueId: conv.ConfigValueId,
      ConversationLayers: conv.ConversationLayers,
      ConversationGroups: conv?.ConversationGroups || [],
      ConversationSecurityGroups: conv?.ConversationSecurityGroups || [],
      CustomFields: conv.CustomFields,
      Title: conv.Title,
      Body: conv?.Body || '',
      ProjectId: conv.ProjectId || null,
      ProjectName: conv.ProjectName || '',
      RelatedConversationId: conv.RelatedConversationId || 0,
      RelatedReplyId: 0,
      IsRedFlag: conv?.IsRedFlag || false,
      IsFavorite: false,
      SideMenuText: '',
      Icon: '',
      Tags: conv?.Tags || [],
      Hashtags: [],
      IsEnable: true,
      IsAdmin: CorgeeStorageHelper.isAdmin(),
      IsAdminProject: false,
      OwnedById: conv?.OwnedById || 0,
      DashboardId: conv.DashboardId,
      DashboardGroupId: conv.DashboardGroupId,
      Metadata: {},
      SocketConnectionId: config.SocketConnectionId,
    };
    return payload;
  };

  const createDuplicateRequest = (conv: ConversationDetails) =>
  {
    const payload = {
      ConversationId: conv.ConversationId,
      CreatedAt: conv.CreatedAt,
      CreatedBy: conv.CreatedBy,
      CreatedByID: conv.CreatedByID,
      Attachments: conv.Attachments || [],
      Body: conv.Body,
      ConfigValueId: conv.ConfigValueId,
      ConversationGroups: conv.ConversationGroups,
      ConversationLayers: conv.ConversationLayers,
      Icon: conv.Icon,
      IsAdmin: CorgeeStorageHelper.isAdmin(),
      IsFavorite: conv.IsFavorite,
      IsFromAssignmentReport: false,
      IsRedFlag: conv.IsRedFlag,
      ProjectId: conv.ProjectId,
      ProjectName: conv.ProjectName,
      SideMenuText: conv.SideMenuText,
      SocketConnectionId: config.SocketConnectionId,
      Title: conv.Title,
      UserExternal: false,
      isInfoWindow: false,
      IsProjectConversation: (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9,
      dataLayers: conv.ConversationLayers,
      Tags: [],
      CustomFields: [],
      MilestoneId: conv.ProjectId,
      ConfigValueIdPhase: null,
      ConversationSecurityGroups: null,
      Conversations: [],
      CoreTaskId: null,
      CoreTaskName: null,
      CustomFieldValueImages: [],
      DashboardId: null,
      DefaultGroupId: null,
      DefaultLayersDuplicateProject: null,
      DuplicateCards: duplicateProjectCards,
      EmbeddedImages: null,
      Finished: false,
      HasBeenEdited: false,
      HasPhases: false,
      Hashtags: [],
      Hide: false,
      ImageName: null,
      IsAdminProject: false,
      IsArchived: false,
      IsCustomType: false,
      IsEnable: true,
      LayerCustomFields: conv.LayerCustomFields,
    };
    return payload;
  };

  const handleAddLayerToProject = useCallback(
    async (
      _e: any,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      let projectConversationId =
        layer?.ProjectConversationId ??
        conversation.ProjectConversationId ??
        (selectedProject as any)?.ConversationId;

      if (!projectConversationId && conversation.ProjectId)
      {
        try
        {
          const proj = await apiService.get<any>(
            `Project/GetProject?projectId=${conversation.ProjectId}`,
          );
          projectConversationId = proj?.ConversationId;
        }
        catch (_) { /* best-effort */ }
      }

      if (!projectConversationId) return;

      const layerName = layer.Name || layer.LayerName || '';
      const layerColor = layer.Color || layer.LayerColor || '';

      try
      {
        const projectConv = await apiService.post<any>(
          `conversation/GetConversationById?conversationId=${projectConversationId}`,
          null,
        );
        if (projectConv && projectConv.ConfigValueId !== 15)
        {
          let groups: any[] = [];
          try
          {
            const fetchedGroups = await apiService.get<any[]>(
              `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
            );
            if (Array.isArray(fetchedGroups)) groups = fetchedGroups;
          }
          catch (_) { /* best-effort */ }

          await apiService.post<any>('conversation/updateconversation', {
            ...projectConv,
            CustomFields: [],
            ConversationGroups: groups.length > 0 ? groups : projectConv.ConversationGroups,
            ConversationLayers: [
              ...(projectConv.ConversationLayers || []),
              {
                ConversationId: projectConv.ConversationId,
                LayerId: layer.LayerId,
                LayerName: layerName,
                LayerColor: layerColor,
                isForAdd: true,
                IsActive: 1,
              },
            ],
            SocketConnectionId: socketService.connectionId,
          });
        }
      }
      catch (err)
      {
        console.error('Failed to add layer to project:', err);
      }

      // Layer is already tracked in EditCard's layersSelected state and will be saved on submit.
      // Call the CCL callback to keep its visual selection in sync.
      functions?.onUpdateLayerToCard?.(layer.LayerId);
    },
    [conversation.ProjectConversationId, conversation.ProjectId, selectedProject],
  );

  const onSaveNewCard = async () =>
  {
    if (isUploadingFile || isSaving) return;
    setIsSaving(true);
    setSaveWarning(null);

    const newConv = { ...conversation };

    // The editor is the source of truth for the body; state never holds it.
    newConv.Body = summernoteRef.current?.getContent() ?? bodyRef.current;

    if (!newConv.Title || newConv.Title.trim() === '')
    {
      setShowTitleWarning(true);
      setIsSaving(false);
      return;
    }

    if (byteLength(newConv.Body || '') > MAX_BODY_BYTES)
    {
      setSaveWarning(BODY_TOO_LARGE_MESSAGE);
      setIsSaving(false);
      return;
    }

    if (showLayerWarning || layersSelected.length === 0)
    {
      setShowLayerWarning(true);
      setIsSaving(false);
      return;
    }

    newConv.Tags = getTagsInBody(newConv.Body || '');
    newConv.CustomFields = [];

    if (conversation.RelatedConversationId && !newConv.Body?.includes('originalConversationLink'))
    {
      newConv.Body +=
        '<span><a class="originalConversationLink" data-linked-conversationid="' +
        newConv.RelatedConversationId +
        '">Link to the original conversation</a></span>';
    }

    // Custom Fields
    const customsfieldsOriginal: any[] = [];
    layersCustomFieldsOriginal.forEach(layer =>
    {
      if (layer)
      {
        layer.CustomFields?.forEach((cf: any) =>
        {
          customsfieldsOriginal.push({
            CustomFieldId: cf.CustomFieldId,
            Value: cf.Value,
            CoreTaskID: cf.CoreTaskId,
            FieldTypeId: cf.FieldTypeId,
            AssociatedImage: cf.AssociatedImage,
            CustomFieldValueId: cf.CustomFieldValueId,
          });
        });
      }
    });

    if (layersCustomFieldsChanges && layersCustomFieldsChanges.length > 0)
    {
      layersCustomFieldsChanges.forEach(layer =>
      {
        layer.CustomFields?.forEach((customField: any) =>
        {
          const cfb = customsfieldsOriginal.find(
            e => e.CustomFieldId === customField.CustomFieldId,
          );
          if (cfb)
          {
            if (cfb.Value !== customField.Value)
            {
              newConv.CustomFields.push({
                CustomFieldId: customField.CustomFieldId,
                Value: customField.Value,
                CoreTaskID: customField.CoreTaskId || 0,
                FieldTypeId: customField.FieldType
                  ? customField.FieldType.FieldTypeId
                  : customField.FieldTypeId,
                AssociatedImage: customField.AssociatedImage,
              });
            }
          }
        });
      });
    }

    // Layers
    newConv.ConversationLayers = layersSelected.map(layer => ({
      LayerId: layer.LayerId,
      LayerColor: layer.Color,
      LayerName: layer.Name,
    }));

    // Groups
    newConv.ConversationGroups = [];
    if (newConv.ConfigValueId === 9)
    {
      groupsSelected.forEach((group: any) =>
      {
        newConv.ConversationGroups.push({
          GroupId: group.GroupId || group.Id,
          GroupName: group.Name,
        });
      });
    }
    else
    {
      groups.forEach((group: any) =>
      {
        newConv.ConversationGroups.push({
          GroupId: group.GroupId || group.Id,
          GroupName: group.Name,
        });
      });
    }

    const payload = Object.assign({}, newConv);

    if (config.functions.loadingBarStart)
    {
      config.functions.loadingBarStart();
    }

    try
    {
      let dataResponse;
      if (conversation.duplicateProject)
      {
        const requestDuplicateConversation = createDuplicateRequest(newConv);
        dataResponse = await dashboardService.DuplicateProject(requestDuplicateConversation);
      }
      else if (creatingCard)
      {
        const requestCreateConversation = createRequest(payload);
        dataResponse = await dashboardGroupsService.createConversation(requestCreateConversation);
      }
      else
      {
        dataResponse = await editCardService.UpdateProject(payload);
      }

      // createConversation returns null on failure
      if (!dataResponse)
      {
        setSaveWarning(SAVE_FAILED_MESSAGE);
        setIsSaving(false);
        if (config.functions.loadingBarComplete) config.functions.loadingBarComplete();
        return;
      }

      const data = { ...dataResponse };

      // Close modal immediately — don't wait for secondary API calls
      if (modalRef.current && !isEnabledSaveAndNew) modalRef.current.closeModal();
      setIsSaving(false);

      data.isInfoWindow = newConv.isInfoWindow;
      data.IsImportant = isImportant;

      // Fetch project image data in the background (non-blocking)
      editCardService
        .GetFewDataFromProject(newConv.ProjectId)
        .then(projectDataResponse =>
        {
          if (projectDataResponse.ProjectImage)
          {
            const pi = projectDataResponse.ProjectImage;
            data.ProjectImage = {
              AssociatedImage: {
                ImageExtension: pi.ImageExtension,
                ImageName: pi.ImageName,
                MiniatureName: pi.MiniatureName,
                ThumbnailName: pi.ThumbnailName,
              },
              BasicFieldId: 0,
              ConversationId: null,
              ConversationIsEnable: true,
              ConversationPhaseId: null,
              CoreTaskId: null,
              CoreTaskPhaseId: null,
              CustomFieldId: 0,
              CustomFieldValueId: 0,
              Description: 'Photo',
              FieldTypeId: 10,
              ImageName: null,
              IsDeleted: false,
              LayerId: 0,
              MiniatureName: null,
              Name: 'Photo',
              OrderInLayer: 1,
              PersonID: null,
              PreviousValue: null,
              ProjectId: data.ProjectId,
              SelectedValuesText: null,
              ServiceUrl: '',
              TenantId: pi.TenantId,
              ThumbnailName: pi.ImagePath,
              Value: 0,
              ValueField: null,
              binary: pi.ImagePath,
            };
          }
          else
          {
            data.ProjectImage = null;
          }
        })
        .catch(err => console.warn('Could not get Few Data from project', err))
        .finally(() =>
        {
          if (config.functions.loadingBarComplete) config.functions.loadingBarComplete();
        });

      /*
       * Make a newly created card linkable via [[ straight away.
       *
       * The list comes from localStorage['C2_objStorageConversation'], which was only ever
       * written by ConversationDetailsView.handleInfoWindowChatCache — i.e. it recorded cards
       * whose details had been opened. A card created here therefore didn't appear in an
       * already-open reply editor until it had been opened and the page reloaded.
       *
       * SummernoteCorgee.getCardsLinkedOnBody and ReplyEditor.getCardsLinked both re-read this
       * key on every keystroke after [[, so writing it here is picked up with no remount.
       *
       * ProjectName comes from local state rather than the response: ReplyEditor inserts it
       * into the link text itself (`[[Title - ProjectName]]`), so an empty or undefined value
       * would be written into the reply body. The entry is skipped unless both fields are
       * known, leaving handleInfoWindowChatCache to add it properly later.
       */
      if (creatingCard)
      {
        try
        {
          const createdId = Number(
            (data?.Conversation as any)?.ConversationId ?? (data as any)?.ConversationId ?? 0,
          );
          const createdTitle = (data?.Conversation as any)?.Title || newConv.Title || '';
          const createdProjectName =
            (data?.Conversation as any)?.ProjectName || newConv.ProjectName || '';

          if (createdId > 0 && createdTitle && createdProjectName)
          {
            const raw = localStorage.getItem('C2_objStorageConversation');
            const cards: Array<{ ConversationId: number; Title: string; ProjectName: string }> =
              raw ? JSON.parse(raw) : [];

            if (Array.isArray(cards) && !cards.some(c => c?.ConversationId === createdId))
            {
              if (cards.length >= 100) cards.shift();
              cards.push({
                ConversationId: createdId,
                Title: createdTitle,
                ProjectName: createdProjectName,
              });
              localStorage.setItem('C2_objStorageConversation', JSON.stringify(cards));
            }
          }
        }
        catch (error)
        {
          // Never let a cache write break card creation
          console.error('Could not add the new card to the link cache:', error);
        }
      }

      // Notify parent component to refresh conversation data
      if (config.functions.conversationResponse)
      {
        config.functions.conversationResponse(payload, data);
      }

      // Let any open list pick up the new card, whichever window created it.
      if (creatingCard)
      {
        conversationBus.emit('conversation-created', {
          conversation: (data as any)?.Conversation ?? data,
        });
      }

      // New project created: refresh the left sidebar project list (MainLayout listens).
      if (creatingCard && (newConv.ConfigValueId ?? newConv.ConfigValueID) === 9)
      {
        window.dispatchEvent(new CustomEvent('sideMenuProjectsRefresh'));
      }

      // Notify other open instances (InfoWindow) to reload their custom fields
      if (newConv.ConversationId)
      {
        conversationBus.emit('conversation-updated', {
          conversationId: newConv.ConversationId,
          updates: {
            refreshCustomFields: true,
            ProjectId: newConv.ProjectId ?? null,
            ProjectName: newConv.ProjectName ?? null,
            Title: newConv.Title ?? null,
          },
        });
      }

      // Notify parent conversation to refresh when a related card is created
      if (creatingCard && newConv.RelatedConversationId)
      {
        conversationBus.emit('conversation-updated', {
          conversationId: newConv.RelatedConversationId,
          updates: { refreshCustomFields: true, relatedCardCreated: true },
          newConversationData: data?.Conversation ?? data,
        });
      }

      if (isEnabledSaveAndNew)
      {
        // Keep project, configValueId, layers, groups and custom fields — only clear content fields
        Reset();
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      else
      {
        setConversation({ ...emptyConversation });
        Reset();
      }
    }
    catch (err)
    {
      console.warn(err);
      setSaveWarning(SAVE_FAILED_MESSAGE);
      setIsSaving(false);
      setIsUploadingFile(false);
      if (config.functions.loadingBarComplete) config.functions.loadingBarComplete();
    }
  };

  // Project selection
  const onChangeProject = (selected: any) =>
  {
    setSelectedProject(selected);
    const projectId = selected ? (selected.ProjectId ?? selected.Id) : null;
    const projectName = selected ? (selected.Name || selected.ProjectName || '') : '';
    setConversation((prev: any) => ({
      ...prev,
      ProjectId: projectId,
      ProjectName: projectName,
      MilestoneId: projectId,
    }));

    // Reset custom fields and layers so they reload for the new project
    setLayersCustomFieldsOriginal([]);
    setLayersCustomFieldsChanges([]);
    setLayersSelected([]);
    setShowLayerWarning(false);
    setShowCustomFields(false);
    setTimeout(() => setShowCustomFields(true), 0);
    // Allow the default-layer effect to re-pick for the newly selected project.
    hasAutoPickedLayerRef.current = false;

    if (projectId)
    {
      editCardService.GetGroups(projectId).then(data =>
      {
        const fetchedGroups = data || [];
        setGroups(fetchedGroups);

        if (!creatingCard)
        {
          // Editing an existing card: keep the previously saved groups selected.
          setGroupsSelected(fetchedGroups);
          setGroupIdsSelected(fetchedGroups.map((g: any) => g.GroupId ?? g.Id));
          return;
        }

        // New card: only the default group; the default layer is picked by the effect below.
        const defaultGroups = getDefaultGroups(fetchedGroups);
        setGroupsSelected(defaultGroups);
        setGroupIdsSelected(defaultGroups.map((g: any) => g.GroupId ?? g.Id));
      });
    }
  };

  // Groups interactions
  const onChangeGroup = (newValue: MultiValue<any>) =>
  {
    const itemsSelected = [...newValue];
    setGroupIdsSelected(itemsSelected.map((i: any) => i.GroupId ?? i.Id));
    setGroupsSelected(itemsSelected);

    // Removing a security group must drop any selected layer no longer backed by a group.
    const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;
    if (isProject)
    {
      const selectedGroupIds = new Set(itemsSelected.map((g: any) => g.GroupId ?? g.Id));
      const fullGroups = groups.filter((g: any) => selectedGroupIds.has(g.GroupId ?? g.Id));
      const allowedLayerIds = new Set<number>(
        (conversation.ConversationLayers || [])
          .filter((l: any) => l.LayerId !== 0)
          .map((l: any) => l.LayerId),
      );
      fullGroups.forEach((g: any) =>
        g.GroupLayers?.forEach((l: any) => allowedLayerIds.add(l.LayerId)),
      );

      const remainingLayers = layersSelected.filter((l: any) => allowedLayerIds.has(l.LayerId));
      if (remainingLayers.length !== layersSelected.length)
      {
        setLayersSelected(remainingLayers);
      }
      setShowLayerWarning(remainingLayers.length === 0);
    }
  };

  // Layers interactions (react-select MultiValue)
  const onChangeLayerSelect = (newValue: MultiValue<any>) =>
  {
    const itemsSelected = [...newValue];
    setLayersSelected(itemsSelected);
    setShowLayerWarning(itemsSelected.length === 0);
    if (summernoteRef.current?.getPeopleTagList)
    {
      summernoteRef.current.getPeopleTagList();
    }
  };

  // Available layer options: for projects, derive from SELECTED groups' GroupLayers; otherwise from all groups
  const layerOptions = useMemo(() =>
  {
    const allLayers: any[] = [
      ...(conversation.ConversationLayers || []).filter((l: any) => l.LayerId !== 0),
    ];
    const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;

    // For projects: find the full group objects (with GroupLayers) from the catalog
    // because groupsSelected from GetGroupsByProject may not include GroupLayers
    let sourceGroups: any[];
    if (isProject)
    {
      const selectedGroupIds = new Set(groupsSelected.map((g: any) => g.GroupId ?? g.Id));
      sourceGroups = groups.filter((g: any) => selectedGroupIds.has(g.GroupId ?? g.Id));
    }
    else
    {
      sourceGroups = groups;
    }

    sourceGroups.forEach((group: any) =>
    {
      group.GroupLayers?.forEach((layer: any) =>
      {
        if (!allLayers.some((l: any) => l.LayerId === layer.LayerId))
        {
          allLayers.push(layer);
        }
      });
    });
    return allLayers;
  }, [
    conversation.ConversationLayers,
    conversation.ConfigValueId,
    conversation.ConfigValueID,
    groups,
    groupsSelected,
  ]);

  // New project cards: pre-select the default layer (C2_layerid, else first option) once
  // layerOptions are available. Reactive to avoid async ordering issues.
  useEffect(() =>
  {
    // Non-project cards manage layers via CardConversationLayers.
    const isProject = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;
    if (!creatingCard || !isProject || hasAutoPickedLayerRef.current) return;
    if (layersSelected.length > 0)
    {
      hasAutoPickedLayerRef.current = true;
      return;
    }
    if (layerOptions.length === 0) return;

    const defaultLayerId = Number(CorgeeStorageHelper.layerId());
    const defaultLayer =
      layerOptions.find((l: any) => l.LayerId === defaultLayerId) || layerOptions[0];
    if (defaultLayer)
    {
      hasAutoPickedLayerRef.current = true;
      setLayersSelected([defaultLayer]);
      setShowLayerWarning(false);
    }
  }, [
    creatingCard,
    layerOptions,
    layersSelected,
    conversation.ConfigValueId,
    conversation.ConfigValueID,
  ]);

  const isProjectCard = (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9;
  const modalTitle =
    conversation.RelatedConversationId || !conversation.ConversationId
      ? currentItemType?.Text || conversation.SideMenuText || 'Item'
      : isProjectCard
        ? conversation.duplicateProject
          ? 'Duplicate Project'
          : 'Edit Project'
        : `Edit ${conversation.SideMenuText || 'Item'}`;

  const internalConfig: any = useMemo(() => ({
    showHeader: false,
    disableAutoSave: true,
    isNew: creatingCard ? true : false,
    expandAll: creatingCard,
    apiUrl: config.apiUrl,
    authToken: config.authToken,
    enableCmbProject: conversation.ProjectId == null,
    onLoadDataCallback,
    UpdateDetailsConversationReact,
    SocketConnectionId: config.SocketConnectionId,
  }), [
    creatingCard,
    config.apiUrl,
    config.authToken,
    config.SocketConnectionId,
    conversation.ProjectId,
    onLoadDataCallback,
    UpdateDetailsConversationReact,
  ]);

  const firstLayerColor =
    layersSelected && layersSelected.length > 0
      ? layersSelected[0].LayerColor || layersSelected[0].Color
      : '#ededed';
  const isDark = document.documentElement.classList.contains('dark');

  const inlineSelectStyles: StylesConfig<any, true> = {
    control: base => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      minHeight: '30px',
      backgroundColor: isDark ? '#2d2f2f' : 'transparent',
      cursor: 'text',
      '&:hover': { border: 'none' },
    }),
    valueContainer: base => ({
      ...base,
      padding: '3px 6px',
      flexWrap: 'wrap',
      gap: '3px',
    }),
    multiValue: base => ({
      ...base,
      backgroundColor: isDark ? '#3a3c3c' : '#fff',
      border: `1px solid ${isDark ? '#4a4c4c' : '#c4c4c4'}`,
      borderRadius: '3px',
      margin: '1px',
      fontSize: '11px',
      lineHeight: '1.4',
    }),
    multiValueLabel: base => ({
      ...base,
      color: isDark ? '#e5e0d8' : '#414042',
      padding: '2px 5px',
      fontSize: '11px',
    }),
    multiValueRemove: base => ({
      ...base,
      color: isDark ? '#6e6860' : '#999',
      padding: '0 4px',
      cursor: 'pointer',
      borderLeft: `1px solid ${isDark ? '#4a4c4c' : '#e0e0e0'}`,
      borderRadius: '0 3px 3px 0',
      ':hover': { backgroundColor: '#62accb', color: '#fff' },
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: () => ({ display: 'none' }),
    placeholder: base => ({ ...base, fontSize: '11px', color: isDark ? '#6e6860' : '#aaa' }),
    input: base => ({ ...base, color: isDark ? '#a9a093' : base.color }),
    menu: base => ({
      ...base,
      zIndex: 99999,
      fontSize: '11px',
      backgroundColor: isDark ? '#333535' : '#fff',
      border: isDark ? '1px solid #4a4c4c' : base.border,
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.2)',
      marginTop: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    }),
    option: (base, state) => ({
      ...base,
      padding: '4px 8px',
      fontSize: '11px',
      backgroundColor: state.isFocused ? '#62accb' : isDark ? '#333535' : 'transparent',
      color: state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
      cursor: 'pointer',
    }),
    menuPortal: base => ({ ...base, zIndex: 999999 }),
  };

  // Matches the app's shared ComboBox look (see CustomFields/FieldRenderer.tsx comboBoxStyles)
  const projectSelectStyles: StylesConfig<any, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: '30px',
      borderRadius: '4px',
      fontSize: '13px',
      backgroundColor: isDark ? '#2d2f2f' : base.backgroundColor,
      borderColor: state.isFocused ? '#62accb' : isDark ? '#4a4c4c' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #62accb' : 'none',
      '&:hover': { borderColor: '#62accb' },
    }),
    valueContainer: base => ({ ...base, padding: '0 8px' }),
    dropdownIndicator: base => ({
      ...base,
      padding: '4px',
      color: '#9ca3af',
      '&:hover': { color: '#6b7280' },
    }),
    clearIndicator: base => ({
      ...base,
      padding: '4px',
      color: '#9ca3af',
      '&:hover': { color: '#6b7280' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    input: base => ({ ...base, margin: 0, padding: 0, color: isDark ? '#a9a093' : base.color }),
    singleValue: base => ({
      ...base,
      fontSize: '13px',
      color: isDark ? '#a9a093' : '#374151',
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '13px',
      padding: '6px 10px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      backgroundColor: state.isSelected
        ? '#62accb'
        : state.isFocused
          ? isDark
            ? '#3a3c3c'
            : '#e0f2f8'
          : isDark
            ? '#2d2f2f'
            : '#fff',
      color: state.isSelected ? '#fff' : isDark ? '#a9a093' : '#374151',
      cursor: 'pointer',
      '&:active': { backgroundColor: '#62accb', color: '#fff' },
    }),
    menu: base => ({
      ...base,
      zIndex: 99999,
      marginTop: 2,
      borderRadius: '4px',
      fontSize: '13px',
      backgroundColor: isDark ? '#2d2f2f' : '#fff',
      border: isDark ? '1px solid #4a4c4c' : base.border,
      boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.15)',
    }),
    menuList: base => ({ ...base, padding: '4px 0', maxHeight: '180px', overflowX: 'hidden' }),
    menuPortal: base => ({ ...base, zIndex: 999999 }),
    placeholder: base => ({ ...base, fontSize: '13px', color: isDark ? '#6e6860' : '#9ca3af' }),
  };

  const projectSelectHeader =
    !isProjectCard && projects.length > 0 ? (
      <div style={{ width: '260px' }}>
        <Select
          value={selectedProject}
          options={projects}
          getOptionLabel={(o: any) => o.Name || o.ProjectName || ''}
          getOptionValue={(o: any) => String(o.ProjectId ?? o.Id)}
          onChange={onChangeProject}
          placeholder="Select project..."
          isClearable={true}
          styles={projectSelectStyles as StylesConfig<any, false>}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          isDisabled={disableEditProject}
        />
      </div>
    ) : null;

  return (
    <div className="react-edit-project-window">
      <div id="editProjectInModal" className="tool-icon" onClick={() => {}}></div>
      <CorgeeModal
        className={`react-new-ticket-modal ${conversation.duplicateProject && 'duplicate-project'}`}
        ref={modalRef}
        title={modalTitle}
        headerExtra={projectSelectHeader}
        onClose={() =>
        {
          setConversation({ ...emptyConversation });
          setShowLayerWarning(false);
        }}
        onSave={onSaveNewCard}
        isDraggable={true}
        closeOnEscape={true}
        textButton={`${
          conversation.RelatedConversationId || !conversation.ConversationId
            ? 'Save'
            : (conversation.ConfigValueId ?? conversation.ConfigValueID) === 9
              ? conversation.duplicateProject
                ? 'Duplicate'
                : 'Update'
              : 'Update'
        }`}
        height={680}
        width={640}
        saveButtonColor={firstLayerColor}
        allowSaveAndNewCheckBox={!conversation.ConversationId}
        callBackFunction={(checked: boolean) => setIsEnabledSaveAndNew(checked)}
        isSaving={isSaving || isUploadingFile}
        isDuplicateProject={conversation.duplicateProject}
        editDuplicateCheck={setDuplicateProjectCards}
      >
        <div className="react-corgee-new_card-modal">
          <div className="react-corgee-new_ticket_title-line-newcard">
            {!conversation.duplicateProject ? (
              <>
                <div className="react-corgee-item-icons-newcard">
                  <span
                    className={
                      conversation.Icon
                        ? `identifier-icon-key ${conversation.Icon}`
                        : 'icons-menu-folder'
                    }
                  ></span>
                  {!isProjectCard && !creatingCard && (
                    <label className="custom-checkbox task-item editcard-finished-checkbox">
                      <input
                        type="checkbox"
                        checked={conversation.Finished || false}
                        onChange={onChangeFinished}
                        tabIndex={0}
                      />
                      <span></span>
                    </label>
                  )}
                  <div className="title-card-action-icon red-flag-card-newcard">
                    <span
                      className={`corgee-icon-red-flag enable-help-tooltip ${conversation.IsRedFlag ? 'red' : ''}`}
                      title={conversation.IsRedFlag ? 'Unmark as Redflag' : 'Mark as Redflag'}
                      onClick={onMarkAsRedFlag}
                    ></span>
                  </div>
                </div>
                <div className="react-corgee-item-title-input-newcard">
                  <input
                    className="react-corgee-new_ticket_title"
                    onChange={uptateTitle}
                    type="text"
                    value={conversation.Title || ''}
                    placeholder="Type title here"
                    ref={inputRef}
                  />
                </div>
              </>
            ) : (
              <>
                <span className="corgee-label">New Project Name</span>
                <input
                  className="react-corgee-new_ticket_title"
                  onChange={uptateTitle}
                  type="text"
                  value={conversation.Title || ''}
                  placeholder="Type title here"
                  ref={inputRef}
                />
              </>
            )}
          </div>

          <CorgeeWarning warningText="Enter a valid Title" show={showTitleWarning} />

          {conversation?.HasPhases && (
            <div className="tabsH-wrapper" style={{ marginTop: '10px', marginBottom: '0px' }}>
              <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
                <TabList>
                  <Tab className="react-tabs__tab">All Phases</Tab>
                  {(conversation.Phases || []).map((phase: any, index: number) => (
                    <Tab className="react-tabs__tab" key={phase.ConversationPhaseId || index}>
                      {editingPhaseId === phase.ConversationPhaseId ? (
                        <input
                          autoFocus
                          type="text"
                          style={{
                            border: 'none',
                            background: 'transparent',
                            outline: 'none',
                            width: '100px',
                            fontSize: '13px',
                          }}
                          value={editingPhaseTitle}
                          onChange={e => setEditingPhaseTitle(e.target.value)}
                          onBlur={() => acceptPhaseRename(phase)}
                          onKeyDown={e =>
                          {
                            if (e.key === 'Enter') acceptPhaseRename(phase);
                            if (e.key === 'Escape') setEditingPhaseId(null);
                          }}
                        />
                      ) : (
                        <div
                          onDoubleClick={() =>
                          {
                            setEditingPhaseId(phase.ConversationPhaseId);
                            setEditingPhaseTitle(phase.Title || '');
                          }}
                          title="Double click to rename"
                        >
                          {phase.Title}
                        </div>
                      )}
                    </Tab>
                  ))}
                </TabList>
                <TabPanel />
                {(conversation.Phases || []).map((_: any, idx: number) => (
                  <TabPanel key={`panel-${idx}`} />
                ))}
              </Tabs>
            </div>
          )}

          {showCustomFields && (
            <div className="corgee-new-card-customfields">
              <CustomFields
                section="section"
                config={internalConfig as any}
                item={conversation}
                projectId={conversation.ProjectId}
                configValueId={conversation.ConfigValueId}
                groups={groups}
                phaseSelected={activePhase}
                isConversationDetailsView={false}
                isProjectView={true}
                UpdateCustomFields={UpdateCustomFields}
              />
            </div>
          )}

          {showSummernote && (
            <div className="react-summernote-new-ticket">
              <div className="card-content-container-new-card">
                <SummernoteCorgee
                  ref={summernoteRef}
                  conversationId={conversation.ConversationId}
                  onChangeBody={onChangeSummernote}
                  onUpdateTemporalImportant={onUpdateTemporalImportant}
                  body={conversation.Body}
                  layerColor={firstLayerColor}
                  textHeight={16}
                  layers={layersSelected}
                  api={api}
                  projectId={conversation.ProjectId}
                  configValueId={conversation.ConfigValueId}
                  selectorContainer=".card-content-container-new-card"
                  isAddReply={false}
                  fixedDropDownPosition={false}
                  isImportant={isImportant}
                  groupIdsSelected={groupIdsSelected.join(',')}
                  onDropFiles={onDropFiles}
                />
                <div className="corgee-uploader" style={{ borderColor: firstLayerColor }}>
                  <FileUpload
                    ref={fileUploadRef}
                    initialFiles={conversation.Attachments || conversation.initialFiles || []}
                    onChangeFileUpload={onChangeFileUpload}
                    onRemoveAttachment={onRemoveAttachment}
                    config={api}
                  />
                </div>
              </div>
            </div>
          )}

          <CorgeeWarning warningText={saveWarning ?? ''} show={!!saveWarning} />

          {showGroups && isProjectCard && (
            <div className="publish-layers">
              <div className="publish-layers-widget">
                <div className="dialog-item">
                  <div className="item-security-settings-container">
                    <div className="iss-label-groups">Security Groups</div>
                    <div className="iss-setting">
                      <div
                        className="corgee_com_card-layers-container-widget"
                        style={{ borderColor: firstLayerColor }}
                      >
                        <Select
                          isMulti
                          value={groupsSelected}
                          options={groups}
                          getOptionLabel={(o: any) => o.Name}
                          getOptionValue={(o: any) => String(o.GroupId ?? o.Id)}
                          onChange={onChangeGroup}
                          placeholder="Select a Group..."
                          isClearable={false}
                          closeMenuOnSelect={true}
                          styles={inlineSelectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showGroups && (
            <div className="publish-layers">
              <div className="publish-layers-widget">
                <div className="dialog-item">
                  <div className="item-security-settings-container">
                    <div className="iss-label-layers">Layers</div>
                    <div className="iss-setting">
                      <div
                        className="corgee_com_card-layers-container-widget"
                        style={{ borderColor: firstLayerColor }}
                      >
                        {isProjectCard ? (
                          <Select
                            isMulti
                            value={layersSelected}
                            options={layerOptions}
                            getOptionLabel={(o: any) => o.Name || o.LayerName || ''}
                            getOptionValue={(o: any) => String(o.LayerId)}
                            onChange={onChangeLayerSelect}
                            placeholder="Select a Layer..."
                            isClearable={false}
                            closeMenuOnSelect={true}
                            styles={inlineSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        ) : (
                          <CardConversationLayers
                            key={`ccl-${conversation.ProjectId ?? 0}`}
                            apiConfig={{ url: config.apiUrl, token: config.authToken }}
                            conversation={conversation}
                            disableAutoSave={true}
                            isNew={creatingCard}
                            isInlineWidget={true}
                            selectableConfigLayers={true}
                            customStyles={inlineSelectStyles}
                            onChangedLayers={(updatedConv: any) =>
                            {
                              const selected = updatedConv.ConversationLayers || [];
                              setLayersSelected(selected);
                              setShowLayerWarning(selected.length === 0);
                              if (summernoteRef.current?.getPeopleTagList)
                              {
                                summernoteRef.current.getPeopleTagList();
                              }
                            }}
                            addLayerToCard={(
                              _e: any,
                              layer: any,
                              functions?: any,
                              _convId?: number,
                            ) =>
                            {
                              setPendingAddLayer(layer);
                              setPendingAddLayerCallback(() => functions);
                              addLayerModalRef.current?.openModal();
                            }}
                            addLayerToProject={handleAddLayerToProject}
                          />
                        )}
                      </div>
                      <CorgeeWarning
                        warningText="Select at least one layer."
                        show={showLayerWarning}
                      />
                      <CorgeeWarning
                        warningText="This project will be saved only in the selected security groups that have at least one of the selected layers."
                        show={showGroupWarning}
                      />
                      <CorgeeWarning
                        warningText="Warning: The cards in this project will keep their original layers. Removing these layers will hide the items in the duplicated project."
                        show={conversation.duplicateProject}
                        isDuplicateProject
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CorgeeModal>

      <CorgeeModal
        ref={addLayerModalRef}
        title="Add layer on card"
        textButton="Yes"
        cancelText="No"
        width={700}
        transparentBackdrop={true}
        onClose={() =>
        {
          setPendingAddLayer(null);
          setPendingAddLayerCallback(null);
        }}
        onSave={handleConfirmAddLayer}
        saveButtonColor="#62accb"
      >
        <div style={{ paddingTop: '10px' }}>
          <p style={{ marginBottom: '10px' }}>
            You must have {pendingAddLayer?.Name || pendingAddLayer?.LayerName} layer on both this
            card and it&apos;s project card to be able to post in that layer
          </p>
          <p>Do you want to add the layer to the project as well as this card?</p>
        </div>
      </CorgeeModal>

      <CorgeeModal></CorgeeModal>
    </div>
  );
});

export default EditCard;
